/**
 * hooks/useMapper.tsx - Visual Mapper Hook
 * 
 * CHANGE HISTORY:
 * - 251103_1040 [pending] danielbeer: Initial Visual Mapper hook
 * 
 * Main hook for managing Visual Mapper state and actions.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type {
  Field,
  Mapping,
  MapperState,
  MapperActions,
  MatchSuggestion,
  ValidationResult,
  DragState
} from '../types/index';

// Backtrackr will be passed as an option
interface BacktrackrInstance {
  recordChange: (change: any) => void;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => Promise<any>;
  redo: () => Promise<any>;
}

export interface UseMapperOptions {
  sourceFields: Field[];
  targetFields: Field[];
  initialMappings?: Mapping[];
  onMappingChange?: (mappings: Mapping[]) => void;
  onValidate?: (mappings: Mapping[]) => ValidationResult;
  autoMatch?: boolean;
  allowMultipleTargets?: boolean;
  allowCombine?: boolean;
  backtrackr?: BacktrackrInstance;  // Optional Backtrackr instance
}

export interface UseMapperReturn extends MapperState, MapperActions {}

/**
 * Main hook for Visual Mapper functionality
 */
export function useMapper(options: UseMapperOptions): UseMapperReturn {
  const {
    sourceFields,
    targetFields,
    initialMappings = [],
    onMappingChange,
    onValidate,
    autoMatch: autoMatchOnMount = false,
    allowMultipleTargets = false,
    allowCombine = true,
    backtrackr
  } = options;

  // State
  const [mappings, setMappings] = useState<Mapping[]>(initialMappings);
  const [selectedMappings, setSelectedMappings] = useState<string[]>([]);
  const [dragState, setDragState] = useState<DragState>({ isDragging: false });
  const [validation, setValidation] = useState<ValidationResult | undefined>();
  const [isAutoMatching, setIsAutoMatching] = useState(false);
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);

  // Generate unique ID
  const generateId = useCallback(() => {
    return `mapping_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Add mapping
  const addMapping = useCallback((mapping: Omit<Mapping, 'id'>) => {
    const newMapping: Mapping = {
      ...mapping,
      id: generateId(),
      enabled: mapping.enabled ?? true,
      validated: false
    };

    setMappings(prev => {
      const oldMappings = [...prev];
      let newMappings: Mapping[];
      
      // Check if target already mapped (unless allowMultipleTargets)
      if (!allowMultipleTargets) {
        const existingIndex = prev.findIndex(m => m.targetField === newMapping.targetField);
        if (existingIndex >= 0) {
          // Replace existing mapping
          newMappings = [...prev];
          newMappings[existingIndex] = newMapping;
        } else {
          newMappings = [...prev, newMapping];
        }
      } else {
        newMappings = [...prev, newMapping];
      }
      
      // Record change for undo
      if (backtrackr) {
        backtrackr.recordChange({
          type: 'mapping-add',
          description: `Map ${mapping.sourceFields.join(', ')} → ${newMapping.targetField}`,
          context: 'visual-mapper',
          before: oldMappings,
          after: newMappings,
          undo: () => setMappings(oldMappings),
          redo: () => setMappings(newMappings)
        });
      }
      
      return newMappings;
    });
  }, [generateId, allowMultipleTargets, backtrackr]);

  // Remove mapping
  const removeMapping = useCallback((mappingId: string) => {
    setMappings(prev => {
      const oldMappings = [...prev];
      const newMappings = prev.filter(m => m.id !== mappingId);
      
      // Record change for undo
      if (backtrackr) {
        const removedMapping = oldMappings.find(m => m.id === mappingId);
        backtrackr.recordChange({
          type: 'mapping-remove',
          description: `Remove mapping ${removedMapping?.targetField}`,
          context: 'visual-mapper',
          before: oldMappings,
          after: newMappings,
          undo: () => setMappings(oldMappings),
          redo: () => setMappings(newMappings)
        });
      }
      
      return newMappings;
    });
    setSelectedMappings(prev => prev.filter(id => id !== mappingId));
  }, [backtrackr]);

  // Update mapping
  const updateMapping = useCallback((mappingId: string, updates: Partial<Mapping>) => {
    setMappings(prev => {
      const oldMappings = [...prev];
      const newMappings = prev.map(m => 
        m.id === mappingId ? { ...m, ...updates, validated: false } : m
      );
      
      // Record change for undo
      if (backtrackr) {
        backtrackr.recordChange({
          type: 'mapping-update',
          description: `Update mapping ${mappingId}`,
          context: 'visual-mapper',
          before: oldMappings,
          after: newMappings,
          undo: () => setMappings(oldMappings),
          redo: () => setMappings(newMappings)
        });
      }
      
      return newMappings;
    });
  }, [backtrackr]);

  // Clear all mappings
  const clearMappings = useCallback(() => {
    setMappings(prev => {
      const oldMappings = [...prev];
      const newMappings: Mapping[] = [];
      
      // Record change for undo
      if (backtrackr) {
        backtrackr.recordChange({
          type: 'mapping-clear',
          description: 'Clear all mappings',
          context: 'visual-mapper',
          before: oldMappings,
          after: newMappings,
          undo: () => setMappings(oldMappings),
          redo: () => setMappings(newMappings)
        });
      }
      
      return newMappings;
    });
    setSelectedMappings([]);
    setValidation(undefined);
  }, [backtrackr]);

  // Calculate field name similarity (0-100)
  const calculateSimilarity = useCallback((name1: string, name2: string): number => {
    const n1 = name1.toLowerCase().replace(/[^a-z0-9]/g, '');
    const n2 = name2.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    // Exact match
    if (n1 === n2) return 100;
    
    // Contains match
    if (n1.includes(n2) || n2.includes(n1)) return 80;
    
    // Levenshtein distance
    const matrix: number[][] = [];
    for (let i = 0; i <= n1.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= n2.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= n1.length; i++) {
      for (let j = 1; j <= n2.length; j++) {
        const cost = n1[i - 1] === n2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }
    
    const distance = matrix[n1.length][n2.length];
    const maxLength = Math.max(n1.length, n2.length);
    const similarity = ((maxLength - distance) / maxLength) * 100;
    
    return Math.round(similarity);
  }, []);

  // Check type compatibility
  const areTypesCompatible = useCallback((sourceType: string, targetType: string): boolean => {
    if (sourceType === targetType) return true;
    if (targetType === 'any') return true;
    if (sourceType === 'any') return true;
    
    // String can convert to most types
    if (sourceType === 'string') {
      return ['number', 'boolean', 'date', 'datetime', 'email', 'phone', 'url'].includes(targetType);
    }
    
    // Number can convert to string
    if (sourceType === 'number' && targetType === 'string') return true;
    
    // Date/datetime are compatible
    if ((sourceType === 'date' || sourceType === 'datetime') && 
        (targetType === 'date' || targetType === 'datetime')) return true;
    
    return false;
  }, []);

  // Auto-match fields
  const autoMatch = useCallback(() => {
    setIsAutoMatching(true);
    
    const newSuggestions: MatchSuggestion[] = [];
    const newMappings: Mapping[] = [];
    
    // Track which targets are already mapped
    const mappedTargets = new Set<string>();
    
    // First pass: exact name matches
    for (const source of sourceFields) {
      for (const target of targetFields) {
        if (mappedTargets.has(target.id)) continue;
        
        const similarity = calculateSimilarity(source.name, target.name);
        const typesCompatible = areTypesCompatible(source.type, target.type);
        
        if (similarity === 100 && typesCompatible) {
          newMappings.push({
            id: generateId(),
            sourceFields: [source.id],
            targetField: target.id,
            confidence: 100,
            validated: false,
            enabled: true
          });
          mappedTargets.add(target.id);
          break;
        }
      }
    }
    
    // Second pass: high similarity matches (>70%)
    for (const source of sourceFields) {
      // Skip if already mapped
      if (newMappings.some(m => m.sourceFields.includes(source.id))) continue;
      
      for (const target of targetFields) {
        if (mappedTargets.has(target.id)) continue;
        
        const similarity = calculateSimilarity(source.name, target.name);
        const typesCompatible = areTypesCompatible(source.type, target.type);
        
        if (similarity > 70 && typesCompatible) {
          newSuggestions.push({
            sourceField: source.id,
            targetField: target.id,
            confidence: similarity,
            reason: `Name similarity: ${similarity}%`
          });
        }
      }
    }
    
    setMappings(newMappings);
    setSuggestions(newSuggestions.sort((a, b) => b.confidence - a.confidence));
    setIsAutoMatching(false);
  }, [sourceFields, targetFields, calculateSimilarity, areTypesCompatible, generateId]);

  // Validate mappings
  const validate = useCallback((): ValidationResult => {
    const errors: ValidationResult['errors'] = [];
    const warnings: ValidationResult['warnings'] = [];
    
    // Check for required target fields
    for (const target of targetFields) {
      if (target.required) {
        const mapped = mappings.some(m => m.targetField === target.id && m.enabled);
        if (!mapped) {
          errors?.push({
            code: 'REQUIRED_FIELD_UNMAPPED',
            message: `Required field "${target.name}" is not mapped`,
            fieldId: target.id
          });
        }
      }
    }
    
    // Check for type compatibility
    for (const mapping of mappings) {
      if (!mapping.enabled) continue;
      
      const source = sourceFields.find(f => f.id === mapping.sourceFields[0]);
      const target = targetFields.find(f => f.id === mapping.targetField);
      
      if (source && target) {
        if (!areTypesCompatible(source.type, target.type)) {
          warnings?.push({
            code: 'TYPE_MISMATCH',
            message: `Type mismatch: ${source.type} → ${target.type}`,
            mappingId: mapping.id
          });
        }
      }
    }
    
    // Check for duplicate target mappings (if not allowed)
    if (!allowMultipleTargets) {
      const targetCounts = new Map<string, number>();
      for (const mapping of mappings) {
        if (!mapping.enabled) continue;
        const count = targetCounts.get(mapping.targetField) || 0;
        targetCounts.set(mapping.targetField, count + 1);
      }
      
      for (const [targetId, count] of targetCounts) {
        if (count > 1) {
          const target = targetFields.find(f => f.id === targetId);
          errors?.push({
            code: 'DUPLICATE_TARGET',
            message: `Field "${target?.name}" is mapped multiple times`,
            fieldId: targetId
          });
        }
      }
    }
    
    const result: ValidationResult = {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
      warnings: warnings.length > 0 ? warnings : undefined
    };
    
    // Use custom validation if provided
    if (onValidate) {
      const customResult = onValidate(mappings);
      result.valid = result.valid && customResult.valid;
      if (customResult.errors) {
        result.errors = [...(result.errors || []), ...customResult.errors];
      }
      if (customResult.warnings) {
        result.warnings = [...(result.warnings || []), ...customResult.warnings];
      }
    }
    
    setValidation(result);
    return result;
  }, [mappings, sourceFields, targetFields, allowMultipleTargets, areTypesCompatible, onValidate]);

  // Apply suggestion
  const applySuggestion = useCallback((suggestion: MatchSuggestion) => {
    addMapping({
      sourceFields: [suggestion.sourceField],
      targetField: suggestion.targetField,
      confidence: suggestion.confidence,
      transform: suggestion.transform,
      validated: false,
      enabled: true
    });
    
    // Remove applied suggestion
    setSuggestions(prev => prev.filter(s => 
      s.sourceField !== suggestion.sourceField || s.targetField !== suggestion.targetField
    ));
  }, [addMapping]);

  // Selection management
  const selectMapping = useCallback((mappingId: string, multi = false) => {
    setSelectedMappings(prev => {
      if (multi) {
        return prev.includes(mappingId) ? prev : [...prev, mappingId];
      }
      return [mappingId];
    });
  }, []);

  const deselectMapping = useCallback((mappingId: string) => {
    setSelectedMappings(prev => prev.filter(id => id !== mappingId));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMappings([]);
  }, []);

  // Auto-match on mount if enabled
  useEffect(() => {
    if (autoMatchOnMount && sourceFields.length > 0 && targetFields.length > 0) {
      autoMatch();
    }
  }, [autoMatchOnMount]); // Only run once on mount

  // Notify parent of mapping changes
  useEffect(() => {
    if (onMappingChange) {
      onMappingChange(mappings);
    }
  }, [mappings, onMappingChange]);

  return {
    // State
    mappings,
    selectedMappings,
    dragState,
    validation,
    isAutoMatching,
    suggestions,
    
    // Actions
    addMapping,
    removeMapping,
    updateMapping,
    clearMappings,
    autoMatch,
    validate,
    applySuggestion,
    selectMapping,
    deselectMapping,
    clearSelection
  };
}
